import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {kanbanEdgeScrollVelocity} from "./kanban-auto-scroll";

export type KanbanColumn = {
  key: string;
  label: string;
  statuses: string[];
  tone?: "default" | "info" | "warning" | "success" | "danger";
  color?: string;
};

export function KanbanBoard<T extends { id: string }>({
  columns,
  rows,
  getStatus,
  renderCard,
  onOpen,
  onStatusChange,
  onCardDragStart,
  onCardDragEnd,
}: {
  columns: KanbanColumn[];
  rows: T[];
  getStatus: (row: T) => string;
  renderCard: (row: T) => ReactNode;
  onOpen: (row: T) => void;
  onStatusChange?: (row: T, status: string, comment: string) => void | Promise<void>;
  onCardDragStart?: (row: T) => void;
  onCardDragEnd?: () => void;
}) {
  const boardRef = useRef<HTMLDivElement>(null);
  const pan = useRef({ active: false, x: 0, left: 0 });
  const draggedRow = useRef<T | undefined>(undefined);
  const edgeScroll = useRef({frame:0,velocity:0,lastFrame:0});
  const [viewportHeight,setViewportHeight]=useState<number>();
  const [pendingMove,setPendingMove]=useState<{row:T;status:string;columnLabel:string}>();
  const [comment,setComment]=useState("");
  const [saving,setSaving]=useState(false);
  const startPan = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("[data-kanban-card]")) return;
    const board = boardRef.current;
    if (!board) return;
    pan.current = { active: true, x: event.pageX, left: board.scrollLeft };
    board.classList.add("is-panning");
  };
  const movePan = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!pan.current.active || !boardRef.current) return;
    boardRef.current.scrollLeft = pan.current.left - (event.pageX - pan.current.x);
  };
  const endPan = () => {
    pan.current.active = false;
    boardRef.current?.classList.remove("is-panning");
  };
  const stopEdgeScroll=()=>{
    if(edgeScroll.current.frame)cancelAnimationFrame(edgeScroll.current.frame);
    edgeScroll.current={frame:0,velocity:0,lastFrame:0};
    boardRef.current?.classList.remove("is-edge-scrolling");
  };
  const runEdgeScroll=(timestamp:number)=>{
    const board=boardRef.current,state=edgeScroll.current;
    if(!board||!draggedRow.current||!state.velocity){stopEdgeScroll();return}
    const elapsed=state.lastFrame?Math.min(timestamp-state.lastFrame,32):16;
    state.lastFrame=timestamp;
    const previous=board.scrollLeft;
    const maximum=Math.max(0,board.scrollWidth-board.clientWidth);
    board.scrollLeft=Math.min(maximum,Math.max(0,previous+state.velocity*(elapsed/1000)));
    if(Math.abs(board.scrollLeft-previous)<.5){stopEdgeScroll();return}
    state.frame=requestAnimationFrame(runEdgeScroll);
  };
  const updateEdgeScroll=(clientX:number)=>{
    const board=boardRef.current;
    if(!board||!draggedRow.current)return;
    const bounds=board.getBoundingClientRect();
    const velocity=kanbanEdgeScrollVelocity({
      pointerX:clientX,
      containerLeft:bounds.left,
      containerRight:bounds.right,
      canScrollLeft:board.scrollLeft>1,
      canScrollRight:board.scrollLeft+board.clientWidth<board.scrollWidth-1,
    });
    edgeScroll.current.velocity=velocity;
    if(!velocity){stopEdgeScroll();return}
    board.classList.add("is-edge-scrolling");
    if(!edgeScroll.current.frame)edgeScroll.current.frame=requestAnimationFrame(runEdgeScroll);
  };
  useEffect(()=>stopEdgeScroll,[]);
  useEffect(()=>{
    const board=boardRef.current;
    if(!board)return;
    let frame=0;
    const updateHeight=()=>{
      cancelAnimationFrame(frame);
      frame=requestAnimationFrame(()=>{
        const top=board.getBoundingClientRect().top;
        const available=Math.max(280,Math.floor(window.innerHeight-top-24));
        setViewportHeight(current=>current===available?current:available);
      });
    };
    updateHeight();
    window.addEventListener("resize",updateHeight);
    const observer=new ResizeObserver(updateHeight);
    if(board.parentElement)observer.observe(board.parentElement);
    return()=>{cancelAnimationFrame(frame);window.removeEventListener("resize",updateHeight);observer.disconnect()};
  },[columns.length]);
  const closeMove=()=>{if(saving)return;setPendingMove(undefined);setComment("")};
  const confirmMove=async()=>{
    if(!pendingMove||comment.trim().length<3||!onStatusChange)return;
    setSaving(true);
    try{await onStatusChange(pendingMove.row,pendingMove.status,comment.trim());setPendingMove(undefined);setComment("")}catch{/* A tela mantém o diálogo aberto e o módulo exibe a mensagem de erro. */}
    finally{setSaving(false)}
  };
  return (
    <>
    <div ref={boardRef} className="kanban-board" style={viewportHeight?{height:viewportHeight}:undefined} onMouseDown={startPan} onMouseMove={movePan} onMouseUp={endPan} onMouseLeave={endPan} onDragOver={event=>updateEdgeScroll(event.clientX)} onDragLeave={event=>{if(event.target===event.currentTarget)stopEdgeScroll()}} onDrop={stopEdgeScroll}>
      {columns.map((column) => {
        const items = rows.filter((row) => column.statuses.includes(getStatus(row)));
        const destinationStatus = column.statuses[0];
        return (
          <section className={`kanban-column tone-${column.tone || "default"}`} style={column.color?({"--kanban-column-color":column.color} as CSSProperties):undefined} key={column.key}>
            <header><strong>{column.label}</strong><span>{items.length}</span></header>
            <div
              className="kanban-column-content"
              onDragOver={(event) => onStatusChange && event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                const row = draggedRow.current;
                draggedRow.current = undefined;
                if (row && getStatus(row) !== destinationStatus) setPendingMove({row,status:destinationStatus,columnLabel:column.label});
              }}
            >
              {items.map((row) => (
                <article
                  data-kanban-card
                  role="button"
                  tabIndex={0}
                  draggable={Boolean(onStatusChange)}
                  key={row.id}
                  onDragStart={(event) => { draggedRow.current = row; event.dataTransfer.effectAllowed = "move"; onCardDragStart?.(row); }}
                  onDragEnd={() => { stopEdgeScroll(); draggedRow.current = undefined; onCardDragEnd?.(); }}
                  onClick={() => onOpen(row)}
                  onKeyDown={event=>{if(event.key==="Enter"||event.key===" "){event.preventDefault();onOpen(row)}}}
                >
                  {renderCard(row)}
                </article>
              ))}
            </div>
          </section>
        );
      })}
    </div>
    {pendingMove&&<div className="dialog-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)closeMove()}}><section className="kanban-status-dialog" role="dialog" aria-modal="true" aria-labelledby="kanban-move-dialog-title"><span className="section-kicker">Alterar status</span><h2 id="kanban-move-dialog-title">Confirmar movimentação</h2><p>Informe o comentário obrigatório para mover este item para <b>{pendingMove.columnLabel}</b>.</p><label><span>Comentário</span><textarea autoFocus required minLength={3} value={comment} onChange={event=>setComment(event.target.value)} placeholder="Descreva o motivo da alteração"/></label><div className="actions"><button type="button" className="button" disabled={saving} onClick={closeMove}>Cancelar</button><button type="button" className="button primary" disabled={saving||comment.trim().length<3} onClick={()=>void confirmMove()}>{saving?"Alterando…":"Confirmar alteração"}</button></div></section></div>}
    </>
  );
}
